import BoltIcon from "@mui/icons-material/Bolt"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import PendingIcon from "@mui/icons-material/Pending"
import { Collapse } from "@mui/material"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Typography
} from "@mui/material"
import { AiMessageSteps } from "__generated__/graphql"
import { useMemo, useState } from "react"
import {
  buildSkillTimeline,
  SkillCycle,
  SkillEndReason,
  SkillRun,
  SkillStepGroup,
  SkillTrigger,
  summarizeResult,
  tokenizeInstruction
} from "./skillRunTimeline.utils"

const COMMAND_COLORS: Record<
  string,
  "default" | "primary" | "secondary" | "success" | "warning" | "info" | "error"
> = {
  RESPOND: "success",
  CALL_ACTION: "primary",
  SEARCH_KNOWLEDGE: "primary",
  COMPLETE: "success",
  END: "success",
  HANDOFF_TEAM: "warning",
  HANDOFF_CPERSON: "warning",
  RUN_SKILL: "secondary",
  SWITCH_SKILL: "secondary",
  RETURN_TO_QPIEN_AI: "info",
  NO_RESPONSE: "default",
  UPDATE_ATTRIBUTE: "primary"
}

const seconds = (ms: number) => `${(ms / 1000).toFixed(2)} s`

function InstructionLine({ text }: { text: string }) {
  return (
    <Typography
      variant="caption"
      component="div"
      sx={{
        color: "text.primary",
        lineHeight: 1.6,
        overflowWrap: "anywhere"
      }}
    >
      {tokenizeInstruction(text).map((token, index) =>
        token.type === "text" ? (
          <span key={index}>{token.value}</span>
        ) : (
          <Box
            key={index}
            component="span"
            sx={{
              display: "inline-block",
              px: 0.6,
              mx: 0.25,
              borderRadius: 1,
              bgcolor: "secondary.main",
              color: "common.white",
              fontSize: 10,
              lineHeight: 1.6,
              verticalAlign: "baseline"
            }}
          >
            {token.label}
          </Box>
        )
      )}
    </Typography>
  )
}

function CycleRow({ cycle }: { cycle: SkillCycle }) {
  const resultSummary = summarizeResult(cycle.action?.result)

  return (
    <Box
      sx={{
        borderLeft: 2,
        borderColor: cycle.failed ? "error.light" : "divider",
        pl: 1.25,
        py: 0.75,
        minWidth: 0,
        overflowWrap: "anywhere"
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          flexWrap: "wrap"
        }}
      >
        <Chip
          label={cycle.command}
          size="small"
          color={
            cycle.failed
              ? "error"
              : (COMMAND_COLORS[cycle.command] ?? "default")
          }
          sx={{ height: 20, fontSize: 11 }}
        />
        <Typography variant="caption" color="text.secondary">
          {seconds(cycle.durationMs)}
          {cycle.usage
            ? ` · ${cycle.usage.inputToken}/${cycle.usage.outputToken} tk`
            : ""}
        </Typography>
        {cycle.stepOutcome === "COMPLETED" && (
          <Typography variant="caption" sx={{ color: "success.main" }}>
            adım tamamlandı
          </Typography>
        )}
        {cycle.waitForUser && (
          <Typography variant="caption" sx={{ color: "info.main" }}>
            müşteri yanıtı bekleniyor
          </Typography>
        )}
      </Box>

      {cycle.instructions.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block" }}
          >
            İşlediği talimat
          </Typography>
          {cycle.instructions.map((line) => (
            <InstructionLine key={line.id} text={line.text} />
          ))}
        </Box>
      )}

      {cycle.rationale && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.5, fontStyle: "italic" }}
        >
          “{cycle.rationale}”
        </Typography>
      )}

      {cycle.action && (
        <Box
          sx={{
            mt: 0.75,
            p: 0.75,
            borderRadius: 1,
            minWidth: 0,
            bgcolor: "action.hover"
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ display: "block" }}
          >
            {cycle.action.name ?? "Aksiyon"}
          </Typography>
          {resultSummary && (
            <Typography variant="caption" sx={{ display: "block" }}>
              {resultSummary}
            </Typography>
          )}
          {cycle.action.result !== undefined && (
            <Accordion
              disableGutters
              elevation={0}
              sx={{ bgcolor: "transparent", "&::before": { display: "none" } }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                sx={{
                  minHeight: 0,
                  p: 0,
                  "& .MuiAccordionSummary-content": { my: 0.5 }
                }}
              >
                <Typography variant="caption" color="primary">
                  Tam sonuç
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1,
                    fontSize: 11,
                    maxHeight: 220,
                    overflowY: "auto",
                    overflowWrap: "anywhere",
                    bgcolor: "grey.100",
                    borderRadius: 1,
                    whiteSpace: "pre-wrap"
                  }}
                >
                  {JSON.stringify(cycle.action.result, null, 2)}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      )}

      {cycle.response && (
        <Box
          sx={{
            mt: 0.75,
            p: 0.75,
            borderRadius: 1,
            bgcolor: "success.light",
            color: "success.contrastText"
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ display: "block" }}
          >
            Müşteriye giden metin
          </Typography>
          <Typography variant="caption">{cycle.response}</Typography>
        </Box>
      )}
    </Box>
  )
}

const END_LABELS: Record<SkillEndReason, { text: string; color: string }> = {
  finished: { text: "Skill turu bu adımda bitirdi", color: "info.main" },
  waiting: { text: "Bu adımda müşteri yanıtı bekleniyor", color: "info.main" },
  stopped: {
    text: "Skill bu adımda kaldı, adımı bitiremedi",
    color: "warning.dark"
  },
  error: { text: "Skill bu adımda hata aldı", color: "error.main" }
}

function StepGroup({
  group,
  index,
  endReason
}: {
  group: SkillStepGroup
  index: number
  endReason?: SkillEndReason
}) {
  const statusIcon = () => {
    if (endReason === "error") {
      return <ErrorOutlineIcon sx={{ fontSize: 16, color: "error.main" }} />
    }
    if (group.completed) {
      return <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
    }
    if (endReason === "stopped") {
      return <ErrorOutlineIcon sx={{ fontSize: 16, color: "warning.main" }} />
    }
    return <PendingIcon sx={{ fontSize: 16, color: "text.disabled" }} />
  }

  const [open, setOpen] = useState(true)

  return (
    <Box sx={{ display: "flex", gap: 1, mb: 1.25, minWidth: 0 }}>
      <Box
        sx={{
          width: 22,
          height: 22,
          flexShrink: 0,
          borderRadius: "50%",
          bgcolor: group.completed ? "success.main" : "grey.400",
          color: "common.white",
          fontSize: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        {group.order ?? index + 1}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          onClick={() => setOpen(!open)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            borderRadius: 1,
            "&:hover": { bgcolor: "action.hover" }
          }}
        >
          {statusIcon()}
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ minWidth: 0, flex: 1, overflowWrap: "anywhere" }}
          >
            {group.title}
          </Typography>
          <ExpandMoreIcon
            sx={{
              fontSize: 18,
              color: "text.disabled",
              transform: open ? "none" : "rotate(-90deg)",
              transition: "transform 120ms"
            }}
          />
        </Box>
        {endReason && (
          <Typography
            variant="caption"
            sx={{ display: "block", color: END_LABELS[endReason].color }}
          >
            {END_LABELS[endReason].text}
          </Typography>
        )}
        <Collapse in={open} timeout={120} unmountOnExit>
          <Box sx={{ mt: 0.5 }}>
            {group.cycles.map((cycle, cycleIndex) => (
              <CycleRow key={`${cycle.stepId}-${cycleIndex}`} cycle={cycle} />
            ))}
          </Box>
        </Collapse>
      </Box>
    </Box>
  )
}

function TriggerLine({ trigger }: { trigger: SkillTrigger }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Tetikleme
        {trigger.confidence !== undefined
          ? ` · güven %${Math.round(trigger.confidence * 100)}`
          : ""}
      </Typography>
      {trigger.reason && (
        <Typography
          variant="caption"
          sx={{ display: "block", fontStyle: "italic" }}
        >
          “{trigger.reason}”
        </Typography>
      )}
    </Box>
  )
}

function RunCard({ run }: { run: SkillRun }) {
  const [open, setOpen] = useState(true)

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        p: 1.5,
        mb: 1.5,
        minWidth: 0,
        bgcolor: "background.paper"
      }}
    >
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minWidth: 0,
          cursor: "pointer",
          borderRadius: 1,
          "&:hover": { bgcolor: "action.hover" }
        }}
      >
        <BoltIcon
          sx={{ fontSize: 18, color: "secondary.main", flexShrink: 0 }}
        />
        <Typography
          variant="body2"
          fontWeight={700}
          noWrap
          sx={{ flex: 1, minWidth: 0 }}
        >
          {run.skillName ?? "Skill"}
        </Typography>
        <Chip
          label={run.outcome.label}
          size="small"
          color={run.outcome.tone === "default" ? undefined : run.outcome.tone}
          sx={{ height: 22, flexShrink: 0 }}
        />
        <ExpandMoreIcon
          sx={{
            fontSize: 20,
            color: "text.disabled",
            flexShrink: 0,
            transform: open ? "none" : "rotate(-90deg)",
            transition: "transform 120ms"
          }}
        />
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 0.25, mb: 1 }}
      >
        {run.groups.length}
        {run.stepCount ? `/${run.stepCount}` : ""} adım ·{" "}
        {run.groups.reduce((total, group) => total + group.cycles.length, 0)}{" "}
        tur · {seconds(run.durationMs)} · ${run.usage.amount.toFixed(4)}
      </Typography>

      <Collapse in={open} timeout={120} unmountOnExit>
        {run.trigger && <TriggerLine trigger={run.trigger} />}

        {run.outcome.detail && (
          <Typography
            variant="caption"
            sx={{ display: "block", mb: 1, color: "error.main" }}
          >
            {run.outcome.detail}
          </Typography>
        )}

        {run.groups.map((group, index) => (
          <StepGroup
            key={`${group.id}-${index}`}
            group={group}
            index={index}
            endReason={
              run.endedAt?.groupId === group.id ? run.endedAt.reason : undefined
            }
          />
        ))}
      </Collapse>
    </Box>
  )
}

/**
 * The Skills view of a run: the flat audit steps rebuilt into the loop they came from — which
 * instruction was active, what the agent decided there, what a tool returned, and where it
 * stopped. The raw step list stays below it for everything this does not summarize.
 */
export default function SkillRunTimeline({
  steps
}: {
  steps: AiMessageSteps[] | undefined
}) {
  const timeline = useMemo(() => buildSkillTimeline(steps), [steps])

  if (!timeline.runs.length && !timeline.skippedTrigger) return null

  return (
    <Box sx={{ px: 2, pb: 1.5, minWidth: 0, overflowX: "hidden" }}>
      {timeline.runs.map((run) => (
        <RunCard key={run.runId} run={run} />
      ))}

      {timeline.skippedTrigger && (
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            p: 1.5,
            bgcolor: "background.paper"
          }}
        >
          <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
            Skill çalışmadı
          </Typography>
          <TriggerLine trigger={timeline.skippedTrigger} />
        </Box>
      )}
    </Box>
  )
}
